import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useProjectReport } from '../../hooks/useReports';
import ReportFilters from '../../components/domain/ReportFilters';
import ReportTable from '../../components/domain/ReportTable';
import { Spinner } from '../../components/common/Spinner';
import { MoneyMetricLabel } from '../../components/common/MoneyMetricLabel';
import { formatCurrency } from '../../utils/format';
import type { ProjectReportRow } from '../../services/api/reports';

export default function ProjectReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ from: string; to: string } | null>(null);
  const { data: report, isLoading, error } = useProjectReport(filters);

  // MKS-332 — the API omits cost and margin for anyone who is not admin or
  // finance, so the columns that depend on them are built only when the data
  // is actually there. `report.totals` is the honest probe: a redacted
  // response has no `totalCosts` anywhere, including the footer.
  const hasCosts = report?.totals.totalCosts !== undefined;

  const getProfit = (row: ProjectReportRow) => row.totalBilling - (row.totalCosts ?? 0);

  const baseColumns = [
    {
      key: 'project',
      header: 'Project',
      render: (row: ProjectReportRow) => <span className="font-medium">{row.projectName}</span>,
      sortValue: (row: ProjectReportRow) => row.projectName,
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{row.totalHours.toFixed(1)}</span>,
      sortValue: (row: ProjectReportRow) => row.totalHours,
    },
    {
      key: 'billing',
      header: <MoneyMetricLabel metric="earned" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{formatCurrency(row.totalBilling)}</span>,
      sortValue: (row: ProjectReportRow) => row.totalBilling,
    },
  ];

  // MKS-332 follow-up (Petra, 2026-10-16, item 2) — the ticket asks for
  // Earned · Invoiced · Paid on every money surface; this report had only
  // Earned. Neither is a cost field, so both stay visible to a manager.
  const revenueColumns = [
    {
      key: 'invoiced',
      header: <MoneyMetricLabel metric="invoiced" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{formatCurrency(row.totalInvoiced ?? 0)}</span>,
      sortValue: (row: ProjectReportRow) => row.totalInvoiced ?? 0,
    },
    {
      key: 'paid',
      header: <MoneyMetricLabel metric="paid" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{formatCurrency(row.totalPaid ?? 0)}</span>,
      sortValue: (row: ProjectReportRow) => row.totalPaid ?? 0,
    },
  ];

  const costColumns = [
    {
      key: 'costs',
      header: <MoneyMetricLabel metric="cost" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{formatCurrency(row.totalCosts ?? 0)}</span>,
      sortValue: (row: ProjectReportRow) => row.totalCosts ?? 0,
    },
    {
      key: 'profit',
      // MKS-332: absolute margin, which is what this column always was —
      // Earned − Cost. Named for the formula it uses so it lines up with the
      // Margin % beside it.
      header: <MoneyMetricLabel metric="margin" label="Margin (CZK)" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) => <span>{formatCurrency(getProfit(row))}</span>,
      sortValue: (row: ProjectReportRow) => getProfit(row),
    },
    {
      key: 'margin',
      header: <MoneyMetricLabel metric="margin" align="right" />,
      align: 'right' as const,
      render: (row: ProjectReportRow) =>
        // Null/absent margin means "earned nothing" — a dash, never 0.0 %.
        row.margin === undefined ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className={row.margin >= 0 ? 'text-green-700' : ''}>{row.margin.toFixed(1)}%</span>
        ),
      sortValue: (row: ProjectReportRow) => row.margin ?? 0,
    },
  ];

  const columns = hasCosts
    ? [...baseColumns, ...revenueColumns, ...costColumns]
    : [...baseColumns, ...revenueColumns];

  const getRowClassName = (row: ProjectReportRow) =>
    hasCosts && getProfit(row) < 0 ? 'bg-red-50 text-red-700' : '';

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/reports')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Project Report</h1>
        <p className="mt-1 text-sm text-gray-500">
          Hover any column name for its definition, or read the{' '}
          <Link to="/reports/money-glossary" className="text-primary-600 hover:underline">
            money glossary
          </Link>
          .
        </p>
      </div>

      <ReportFilters onApply={setFilters} isLoading={isLoading} />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{(error as Error).message}</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {report && (
        <ReportTable
          columns={columns}
          rows={report.rows}
          getRowKey={(row) => row.projectId}
          getRowClassName={getRowClassName}
          footer={
            <tr>
              <td className="px-6 py-3 text-sm font-semibold text-gray-900">Total</td>
              <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                {report.totals.totalHours.toFixed(1)}
              </td>
              <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                {formatCurrency(report.totals.totalBilling)}
              </td>
              {/* MKS-332 follow-up: must mirror `revenueColumns` above, or the
                  footer stops lining up with the header it sits under. */}
              <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                {formatCurrency(report.totals.totalInvoiced ?? 0)}
              </td>
              <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                {formatCurrency(report.totals.totalPaid ?? 0)}
              </td>
              {hasCosts && (
                <>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(report.totals.totalCosts ?? 0)}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(report.totals.totalBilling - (report.totals.totalCosts ?? 0))}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {report.totals.margin === undefined
                      ? '—'
                      : `${report.totals.margin.toFixed(1)}%`}
                  </td>
                </>
              )}
            </tr>
          }
        />
      )}
    </div>
  );
}
